import {
  Component,
  ErrorHandler,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { IModalComponent } from '../../models/modal-behaviour.interface';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InputFieldType } from '../../models/input-types.enum';
import { ThirdPartiesApiService } from 'src/app/core/services/api/third-parties-api.service';
import { finalize, take } from 'rxjs';
import { ApiResponse } from '../../models/api-response.interface';
import { ThirdPartyTypeInterface } from '../../models/third-party-type.interface';
import { DBEntity } from '../../models/db-entity.model';
import { STATUSES } from 'src/app/modules/third-parties/consts/statuses.const';
import { NotificationsService } from 'angular2-notifications';
import { ErrorHandlingService } from 'src/app/core/services/error-handling.service';
import { USAPhoneNumberValidator } from '../../utils/validators.util';

@Component({
  selector: 'app-third-party-form-modal',
  templateUrl: './third-party-form.component.html',
  styleUrls: ['./third-party-form.component.scss'],
})
export class ThirdPartyFormComponent extends IModalComponent implements OnInit {
  @Output() onCreated = new EventEmitter<any>();
  @Input() preSelectedTypeId?: number;
  @Input() shouldEmitFullObject? = false;
  @Input() customTitle?: string;
  @Input() formModel?: string;
  thirdPartyForm!: FormGroup;
  thirdPartyTypesOptions: ThirdPartyTypeInterface[] = [];
  thirdPartyTypesOptionsLoading: boolean = false;
  titlesOptions: DBEntity[] = [];
  titlesOptionsLoading: boolean = false;
  isLoading: boolean = false;
  protected readonly STATUS_OPTIONS = STATUSES;
  protected readonly InputFieldType = InputFieldType;

  constructor(
    private fb: FormBuilder,
    private thirdPartiesService: ThirdPartiesApiService,
    private notificationService: NotificationsService,
    private errorHandler: ErrorHandlingService,
  ) {
    super();
  }

  get formAddress(): FormGroup {
    return this.thirdPartyForm.get('formAddress') as FormGroup;
  }

  ngOnInit(): void {
    this.initForm();
    this.getPrerequisets();
  }

  override handleModalHide(): void {
    this.thirdPartyForm.reset();
    // if(this.formModel === 'service_advisor' || this.formModel === 'repair_facility' ){
    //   this.thirdPartyForm.patchValue({
    //     status : 'Inspected'
    //   })
    // }
    if (this.preSelectedTypeId) {
      this.thirdPartyForm.get('type_id')?.setValue(this.preSelectedTypeId);
    }
  }

  handleFormSubmit(): void {
    if (!this.thirdPartyForm.valid) {
      this.notificationService.error(
        'Error',
        'Please ensure all fields are filled correctly',
      );
      this.thirdPartyForm.markAllAsTouched();
      return;
    }
    const formValue = { ...this.thirdPartyForm.value };
    const address = {
      ...formValue?.formAddress,
      address: formValue?.formAddress?.street_address?.label,
    };
    if (
      this.formModel === 'service_advisor' ||
      this.formModel === 'repair_facility'
    ) {
      if (!formValue?.status) {
        delete formValue.status;
      }
    }
    delete formValue.formAddress;
    delete formValue.street_address;
    const thirdParty = {
      ...formValue,
      ...address,
      mobile: `+1${formValue?.mobile}`,
    };
    if (this.formModel === 'service_advisor' && !thirdParty?.name) {
      thirdParty['name'] =
        `${thirdParty?.first_name} ${thirdParty?.last_name} `;
    }
    if (this.preSelectedTypeId === 1) {
      delete thirdParty['name'];
      delete thirdParty['title_id'];
    }
    this.isLoading = true;
    this.thirdPartiesService
      .create(thirdParty)
      .pipe(
        take(1),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (result: ApiResponse) => {
          this.notificationService.success('Success', result.message);
          this.onCreated.emit(
            this.shouldEmitFullObject ? result.data : result.data.id,
          );
          this.toggleModal();
        },
        error: (error: { error: Error }) =>
          this.errorHandler.errorHandling(error),
      });
  }

  private initForm(): void {
    if (this.formModel === 'repair_facility') {
      this.thirdPartyForm = this.fb.group({
        name: [null, Validators.required],
        status: [null],
        type_id: [this.preSelectedTypeId ?? null, Validators.required],
        internal_code: [null],
        title_id: [null],
        first_name: [null],
        middle_name: [null],
        last_name: [null],
        mobile: [null, Validators.required],
        email: [null],
        url: [null],
        formAddress: this.fb.group({
          street_address: [null],
          country: [null],
          state: [null],
          city: [null],
          zip_code: [null],
        }),
      });
    } else if (this.formModel === 'service_advisor') {
      this.thirdPartyForm = this.fb.group({
        status: [null],
        type_id: [this.preSelectedTypeId ?? null, Validators.required],
        internal_code: [null],
        title_id: [null],
        first_name: [null, Validators.required],
        middle_name: [null],
        last_name: [null, Validators.required],
        mobile: [null, Validators.required],
        email: [null],
        url: [null],
        formAddress: this.fb.group({
          street_address: [null],
          country: [null],
          state: [null],
          city: [null],
          zip_code: [null],
        }),
      });
    } else {
      this.thirdPartyForm = this.fb.group({
        name: [null],
        status: ['Inspected', Validators.required],
        type_id: [this.preSelectedTypeId ?? null, Validators.required],
        internal_code: [null],
        title_id: [null],
        first_name: [null, Validators.required],
        middle_name: [null],
        last_name: [null, Validators.required],
        mobile: [null, Validators.required],
        email: [null, [Validators.email]],
        url: [null],
        formAddress: this.fb.group({
          street_address: [null],
          country: [null],
          state: [null],
          city: [null],
          zip_code: [null],
        }),
      });
    }
  }

  private getPrerequisets(): void {
    this.getTypes();
    this.getTitles();
  }

  private getTypes(): void {
    this.thirdPartyTypesOptionsLoading = true;
    this.thirdPartiesService
      .getAllTypes()
      .pipe(
        take(1),
        finalize(() => (this.thirdPartyTypesOptionsLoading = false)),
      )
      .subscribe((result: ThirdPartyTypeInterface[]) => {
        this.thirdPartyTypesOptions = result;
      });
  }

  private getTitles(): void {
    this.titlesOptionsLoading = true;
    this.thirdPartiesService
      .getTitles()
      .pipe(
        take(1),
        finalize(() => (this.titlesOptionsLoading = false)),
      )
      .subscribe((result: ApiResponse<DBEntity[]>) => {
        this.titlesOptions = result.data;
      });
  }
}
